import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, FileText, Users, Layers, Search, Trash2, X, RotateCcw, Wifi, MessageCircle, MapPin, GraduationCap, Cake } from 'lucide-react';
import RyanUserSessionsModal from '../RyanUserSessionsModal';
import { useAuth } from '../../../../contexts/AuthContext';
import './RyanAdminStats.css';

interface Overview {
  totalSpaces: number;
  totalFlashcards: number;
  totalApostilas: number;
  totalUsers: number;
  periods: number[];
}

interface EngagementUser {
  _id: string;
  userId: string;
  accessId: string;
  username: string;
  email: string;
  isVip: boolean;
  isEditor: boolean;
  isOnline: boolean;
  lastSeen: string | null;
  lastStudied: string | null;
  cardsStudiedTotal: number;
  cardsStudiedThisWeek: number;
  apostilasLidas: number;
  grants: { spaces: number; tabs: number; subdecks: number };
  deviceCount: number;
  profilePhoto?: string;
  periods: number[];
  status: 'active' | 'risk' | 'inactive';
  // "Área do aluno" — leads pro WhatsApp do admin (whatsapp coletado
  // obrigatoriamente na criação da conta, ou editado depois em "Área do
  // aluno"; resto vem só de "Área do aluno"/perfil ENEM, quando preenchido).
  whatsapp: string | null;
  birthDate: string | null;
  state: string | null;
  city: string | null;
  intendedCourse: string | null;
}

const tok = () => localStorage.getItem('accessToken') || '';
// 12 = 4 linhas cheias no grid de 3 colunas (e também fecha certo em 2 e 1 coluna)
const PAGE_SIZE = 12;

type SortMode = 'recent' | 'engagement' | 'alpha';

function formatPrice(cents: number): string {
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function formatLastSeen(lastSeen: string | null, lastStudied: string | null): string {
  const date = lastSeen || lastStudied;
  if (!date) return 'Nunca visto';
  const now = Date.now();
  const diff = now - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'Agora mesmo';
  if (mins < 60) return `${mins}m atrás`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h atrás`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d atrás`;
  return new Date(date).toLocaleDateString('pt-BR');
}

// wa.me exige o número com DDI — número "cru" da área do aluno/criação de
// conta só tem DDD+número (10 ou 11 dígitos); assume Brasil (55) nesse caso
// e deixa como está se já vier com DDI (12-13 dígitos, ou colado com +).
function whatsappLink(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${withCountry}`;
}

const STATUS_CONFIG = {
  active:   { label: '● Ativo',    cls: 'ras-status-active' },
  risk:     { label: '● Em risco', cls: 'ras-status-risk' },
  inactive: { label: '● Inativo',  cls: 'ras-status-inactive' },
} as const;

function sessionBadgeColor(count: number): string {
  if (count <= 3) return '#16a34a';
  if (count <= 5) return '#d97706';
  return '#dc2626';
}

function avatarBg(u: EngagementUser): string {
  if (u.isEditor) return 'rgba(139,92,246,0.15)';
  if (u.isVip)    return 'rgba(245,158,11,0.15)';
  return 'rgba(13,59,14,0.1)';
}

function avatarColor(u: EngagementUser): string {
  if (u.isEditor) return '#8b5cf6';
  if (u.isVip)    return '#f59e0b';
  return '#0D3B0E';
}

function userTypeBadge(u: EngagementUser): { label: string; cls: string } {
  if (u.isEditor) return { label: 'Editor',  cls: 'ras-badge--editor' };
  if (u.isVip)    return { label: 'VIP',     cls: 'ras-badge--vip' };
  return              { label: 'Usuário', cls: 'ras-badge--comum' };
}

// ── Count-up component ──────────────────────────────────────────────────────

const CountUp: React.FC<{ target: number; duration?: number }> = ({ target, duration = 800 }) => {
  const [count, setCount] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    if (!target || done.current) return;
    done.current = true;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [target, duration]);

  return <>{count.toLocaleString('pt-BR')}</>;
};

// ── Main Component ──────────────────────────────────────────────────────────

type TypeFilter = 'all' | 'vip' | 'editor' | 'comum';

interface Props {
  onHeaderChange: (title: string, subtitle: string) => void;
}

const RyanAdminStats: React.FC<Props> = ({ onHeaderChange }) => {
  const [overview, setOverview]           = useState<Overview | null>(null);
  const [allUsers, setAllUsers]           = useState<EngagementUser[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingUsers, setLoadingUsers]   = useState(true);

  const [selectedPeriodo, setSelectedPeriodo] = useState<number | 'all'>('all');
  const [search, setSearch]               = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort]                   = useState<SortMode>('recent');
  const { user: currentUser }             = useAuth();
  const [typeFilter, setTypeFilter]       = useState<TypeFilter>('all');
  const [page, setPage]                   = useState(1);

  const [selectedUser, setSelectedUser]   = useState<EngagementUser | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId]       = useState<string | null>(null);
  // Preenchido quando o backend recusa a exclusão de primeira (409) por a
  // conta ter venda concluída ou grant ativo — troca o texto de confirmação
  // pelo aviso real antes de permitir excluir com force:true.
  const [deleteWarning, setDeleteWarning] = useState<{ salesCount: number; totalRevenueCents: number; hasActiveGrants: boolean } | null>(null);
  const [resetConfirm, setResetConfirm]   = useState(false);
  const [resetingId, setResetingId]       = useState<string | null>(null);
  const [toastMsg, setToastMsg]           = useState<string | null>(null);
  const [photoModal, setPhotoModal]       = useState<string | null>(null);
  const [sessionsUser, setSessionsUser]   = useState<{ userId: string; username: string } | null>(null);

  const gridRef   = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onHeaderChange('Estatísticas', 'Visão geral da plataforma e engajamento dos usuários.');
  }, [onHeaderChange]);

  // ── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    fetch('/api/ryan/admin/stats/overview', { headers: { Authorization: `Bearer ${tok()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setOverview(d); })
      .finally(() => setLoadingOverview(false));

    fetch('/api/ryan/admin/stats/engagement', { headers: { Authorization: `Bearer ${tok()}` } })
      .then(r => r.ok ? r.json() : { users: [] })
      .then(d => setAllUsers(d.users || []))
      .finally(() => setLoadingUsers(false));
  }, []);

  // ── Debounce search ───────────────────────────────────────────────────────

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, selectedPeriodo, sort, typeFilter]);

  // ── Filtered + sorted users ───────────────────────────────────────────────

  const filtered = (() => {
    let list = allUsers;

    if (selectedPeriodo !== 'all') {
      list = list.filter(u => u.periods.includes(selectedPeriodo as number));
    }

    if (typeFilter === 'vip')    list = list.filter(u => u.isVip && !u.isEditor);
    if (typeFilter === 'editor') list = list.filter(u => u.isEditor);
    if (typeFilter === 'comum')  list = list.filter(u => !u.isVip && !u.isEditor);

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(u =>
        (u.username || '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }

    if (sort === 'recent') {
      // Você primeiro; depois quem está online; depois o acesso mais recente
      // (lastSeen já vem do backend como o mais recente entre sessão e app)
      const lastAccess = (u: EngagementUser) => {
        const d = u.lastSeen || u.lastStudied;
        return d ? new Date(d).getTime() : 0;
      };
      list = [...list].sort((a, b) => {
        const aMe = a.userId === currentUser?.userId;
        const bMe = b.userId === currentUser?.userId;
        if (aMe !== bMe) return aMe ? -1 : 1;
        if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
        return lastAccess(b) - lastAccess(a);
      });
    } else if (sort === 'alpha') {
      list = [...list].sort((a, b) =>
        (a.username || a.email).localeCompare(b.username || b.email, 'pt-BR')
      );
    } else {
      list = [...list].sort((a, b) => {
        if (a.isOnline && !b.isOnline) return -1;
        if (!a.isOnline && b.isOnline) return 1;
        const statusOrder = { active: 0, risk: 1, inactive: 2 };
        if (statusOrder[a.status] !== statusOrder[b.status]) return statusOrder[a.status] - statusOrder[b.status];
        if (!a.lastStudied) return 1;
        if (!b.lastStudied) return -1;
        return new Date(b.lastStudied).getTime() - new Date(a.lastStudied).getTime();
      });
    }

    return list;
  })();

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageUsers  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goPage = (p: number) => {
    setPage(p);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Reset stats handler ───────────────────────────────────────────────────

  const handleResetStats = useCallback(async (userId: string) => {
    setResetingId(userId);
    try {
      const r = await fetch(`/api/ryan/admin/users/${userId}/reset-stats`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok()}` },
      });
      if (!r.ok) throw new Error('Erro ao resetar');
      setAllUsers(prev => prev.map(u =>
        u._id === userId
          ? { ...u, cardsStudiedTotal: 0, cardsStudiedThisWeek: 0, apostilasLidas: 0 }
          : u
      ));
      setSelectedUser(prev =>
        prev && prev._id === userId
          ? { ...prev, cardsStudiedTotal: 0, cardsStudiedThisWeek: 0, apostilasLidas: 0 }
          : prev
      );
      setResetConfirm(false);
      setToastMsg('Estatísticas resetadas com sucesso.');
    } catch {
      setToastMsg('Erro ao resetar. Tente novamente.');
    } finally {
      setResetingId(null);
      setTimeout(() => setToastMsg(null), 3500);
    }
  }, []);

  // ── Delete handler ────────────────────────────────────────────────────────

  // Primeira chamada sem force: se o backend recusar com 409 (venda
  // concluída ou grant ativo), guarda o aviso em vez de fingir sucesso —
  // um segundo clique (com o aviso já visível) reenvia com force:true.
  const handleDelete = useCallback(async (userId: string, force = false) => {
    setDeletingId(userId);
    try {
      const url = `/api/ryan/admin/users/${userId}${force ? '?force=true' : ''}`;
      const r = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tok()}` },
      });
      const d = await r.json().catch(() => ({}));

      if (r.status === 409 && d.requiresConfirmation) {
        setDeleteWarning({ salesCount: d.salesCount, totalRevenueCents: d.totalRevenueCents, hasActiveGrants: d.hasActiveGrants });
        return;
      }
      if (!r.ok) {
        setToastMsg(d.message || 'Erro ao excluir usuário.');
        setTimeout(() => setToastMsg(null), 4500);
        return;
      }

      setAllUsers(prev => prev.filter(u => u._id !== userId));
      setSelectedUser(null);
      setDeleteConfirm(false);
      setDeleteWarning(null);
    } catch {
      setToastMsg('Erro de conexão ao excluir usuário.');
      setTimeout(() => setToastMsg(null), 4500);
    } finally {
      setDeletingId(null);
    }
  }, []);

  // ── KPI config ────────────────────────────────────────────────────────────

  const kpis = overview ? [
    { icon: Layers,   label: 'Campos',     value: overview.totalSpaces,     bg: 'linear-gradient(135deg, #2F7130 0%, #245a25 100%)' },
    { icon: BookOpen, label: 'Flashcards', value: overview.totalFlashcards, bg: 'linear-gradient(135deg, #1F5620 0%, #17431a 100%)' },
    { icon: FileText, label: 'Apostilas',  value: overview.totalApostilas,  bg: 'linear-gradient(135deg, #184C18 0%, #123a12 100%)' },
    { icon: Users,    label: 'Usuários',   value: overview.totalUsers,      bg: 'linear-gradient(135deg, #0D3B0E 0%, #081f08 100%)' },
  ] : [];

  // ── Pagination range ──────────────────────────────────────────────────────

  const paginationRange = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return Array.from({ length: 5 }, (_, i) => start + i);
  })();

  const TYPE_FILTER_LABELS: Record<TypeFilter, string> = {
    all:    'Todos',
    vip:    'VIP',
    editor: 'Editor',
    comum:  'Comum',
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="ras-root ras-stats-root">

      {/* ── KPI Cards ── */}
      {loadingOverview ? (
        <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
      ) : (
        <div className="ras-kpi-grid">
          {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div
                key={kpi.label}
                className="ras-kpi-card"
                style={{ '--kpi-bg': kpi.bg, animationDelay: `${i * 80}ms` } as React.CSSProperties}
              >
                <div className="ras-kpi-top-row">
                  <div className="ras-kpi-icon-wrap">
                    <Icon size={18} color="#ffffff" />
                  </div>
                  <span className="ras-kpi-label">{kpi.label}</span>
                </div>
                <div className="ras-kpi-number">
                  <CountUp target={kpi.value} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Period Chips ── */}
      {overview && overview.periods.length > 0 && (
        <div className="ras-period-section">
          <div className="ras-period-chips">
            <button
              className={`ras-period-chip${selectedPeriodo === 'all' ? ' active' : ''}`}
              onClick={() => setSelectedPeriodo('all')}
            >
              Todos
            </button>
            {overview.periods.map(p => (
              <button
                key={p}
                className={`ras-period-chip${selectedPeriodo === p ? ' active' : ''}`}
                onClick={() => setSelectedPeriodo(p)}
              >
                {p}°
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Controls ── */}
      <div ref={gridRef} className="ras-list-controls">
        <div className="ras-search-wrap">
          <Search size={14} className="ras-search-icon" />
          <input
            placeholder="Buscar por nome ou email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="ras-type-filter">
          {(Object.keys(TYPE_FILTER_LABELS) as TypeFilter[]).map(t => (
            <button
              key={t}
              className={`ras-type-chip${typeFilter === t ? ' active' : ''}`}
              onClick={() => setTypeFilter(t)}
            >
              {TYPE_FILTER_LABELS[t]}
            </button>
          ))}
        </div>

        <select
          className="ras-sort-select"
          value={sort}
          onChange={e => setSort(e.target.value as SortMode)}
        >
          <option value="recent">Último acesso</option>
          <option value="engagement">Engajamento</option>
          <option value="alpha">Alfabética</option>
        </select>
      </div>

      <div className="ras-user-count-bar">
        Exibindo <strong>{Math.min(pageUsers.length + (page - 1) * PAGE_SIZE, filtered.length)}</strong> de <strong>{filtered.length}</strong> usuário{filtered.length !== 1 ? 's' : ''}
      </div>

      {/* ── User Grid ── */}
      {loadingUsers ? (
        <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="ras-empty">
          <Users size={40} color="#0D3B0E" style={{ opacity: 0.25 }} />
          {debouncedSearch ? (
            <>
              <p>Nenhum usuário encontrado para &ldquo;{debouncedSearch}&rdquo;</p>
              <button className="ras-btn-clear" onClick={() => setSearch('')}>Limpar busca</button>
            </>
          ) : (
            <p>Nenhum usuário com acesso ainda.</p>
          )}
        </div>
      ) : (
        <>
          <div className="ras-user-grid">
            {pageUsers.map((u, i) => {
              const initial = (u.username || u.email)[0].toUpperCase();
              const badge   = userTypeBadge(u);
              const sc      = STATUS_CONFIG[u.status];

              return (
                <div
                  key={u._id}
                  className="ras-user-card"
                  style={{ animationDelay: `${i * 40}ms` }}
                  onClick={() => { setSelectedUser(u); setDeleteConfirm(false); setDeleteWarning(null); }}
                >
                  <div className="ras-uc-top">
                    <div
                      className="ras-uc-avatar"
                      style={{
                        background: avatarBg(u),
                        color: avatarColor(u),
                        cursor: u.profilePhoto ? 'zoom-in' : 'default',
                      }}
                      onClick={u.profilePhoto ? (e) => { e.stopPropagation(); setPhotoModal(u.profilePhoto!); } : undefined}
                    >
                      {u.profilePhoto
                        ? <img src={u.profilePhoto} alt={u.username || u.email} />
                        : initial
                      }
                    </div>
                    <div className="ras-uc-info">
                      <div className="ras-uc-name">{u.username || '—'}</div>
                      <div className="ras-uc-email">{u.email}</div>
                    </div>
                  </div>

                  <div className="ras-uc-badges">
                    <span className={`ras-type-badge ${badge.cls}`}>{badge.label}</span>
                    {u.periods.length > 0 && (
                      <span className="ras-period-badge">{u.periods[0]}° per.</span>
                    )}
                    <button
                      className="ras-sessions-badge"
                      style={{ background: sessionBadgeColor(u.deviceCount) }}
                      onClick={e => {
                        e.stopPropagation();
                        setSessionsUser({ userId: u._id, username: u.username || u.email });
                      }}
                      title="Ver histórico de dispositivos"
                    >
                      <Wifi size={10} />
                      {u.deviceCount}
                    </button>
                  </div>

                  <div className="ras-uc-bottom">
                    {u.isOnline ? (
                      <div className="ras-uc-online">
                        <span className="ras-online-dot" />
                        Online agora
                      </div>
                    ) : (
                      <div className="ras-uc-offline">
                        <span className="ras-offline-dot" />
                        {formatLastSeen(u.lastSeen, u.lastStudied)}
                      </div>
                    )}
                    <div className="ras-uc-right">
                      <span className={sc.cls + ' ras-uc-status'}>{sc.label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ras-pagination">
              <button className="ras-page-btn" onClick={() => goPage(page - 1)} disabled={page === 1}>‹</button>

              {paginationRange[0] > 1 && (
                <>
                  <button className="ras-page-btn" onClick={() => goPage(1)}>1</button>
                  {paginationRange[0] > 2 && <span className="ras-page-ellipsis">…</span>}
                </>
              )}

              {paginationRange.map(p => (
                <button
                  key={p}
                  className={`ras-page-btn${p === page ? ' active' : ''}`}
                  onClick={() => goPage(p)}
                >
                  {p}
                </button>
              ))}

              {paginationRange[paginationRange.length - 1] < totalPages && (
                <>
                  {paginationRange[paginationRange.length - 1] < totalPages - 1 && (
                    <span className="ras-page-ellipsis">…</span>
                  )}
                  <button className="ras-page-btn" onClick={() => goPage(totalPages)}>{totalPages}</button>
                </>
              )}

              <button className="ras-page-btn" onClick={() => goPage(page + 1)} disabled={page === totalPages}>›</button>
            </div>
          )}
        </>
      )}

      {/* ── Sessions modal (portal — escapes stacking context) ── */}
      {sessionsUser && (
        <RyanUserSessionsModal
          userId={sessionsUser.userId}
          username={sessionsUser.username}
          onClose={() => setSessionsUser(null)}
        />
      )}

      {/* ── Photo modal ── */}
      {photoModal && (
        <div className="ras-photo-modal-overlay" onClick={() => setPhotoModal(null)}>
          <div className="ras-photo-modal-content" onClick={e => e.stopPropagation()}>
            <button className="ras-photo-modal-close" onClick={() => setPhotoModal(null)}>
              <X size={18} />
            </button>
            <img src={photoModal} alt="Foto do usuário" className="ras-photo-modal-img" />
          </div>
        </div>
      )}

      {/* ── Detail Drawer ── */}
      {selectedUser && (() => {
        const u       = selectedUser;
        const initial = (u.username || u.email)[0].toUpperCase();
        const badge   = userTypeBadge(u);
        const sc      = STATUS_CONFIG[u.status];
        const isDeleting = deletingId === u._id;

        return (
          <div className="ras-drawer-overlay" onClick={() => { setSelectedUser(null); setDeleteConfirm(false); setResetConfirm(false); }}>
            <div className="ras-drawer" onClick={e => e.stopPropagation()}>
              <div className="ras-drawer-header">
                <div
                  className="ras-drawer-avatar"
                  style={{
                    background: avatarBg(u),
                    color: avatarColor(u),
                    cursor: u.profilePhoto ? 'zoom-in' : 'default',
                  }}
                  onClick={u.profilePhoto ? () => setPhotoModal(u.profilePhoto!) : undefined}
                >
                  {u.profilePhoto
                    ? <img src={u.profilePhoto} alt={u.username || u.email} />
                    : initial
                  }
                </div>
                <div className="ras-drawer-identity">
                  <div className="ras-drawer-name">{u.username || '—'}</div>
                  <div className="ras-drawer-email">{u.email}</div>
                  <div className="ras-drawer-badges">
                    <span className={`ras-type-badge ${badge.cls}`}>{badge.label}</span>
                    {u.periods.length > 0 && (
                      <span className="ras-period-badge">{u.periods.map(p => `${p}°`).join(', ')}</span>
                    )}
                  </div>
                </div>
                <button className="ras-drawer-close" onClick={() => { setSelectedUser(null); setDeleteConfirm(false); setResetConfirm(false); }}>
                  <X size={18} />
                </button>
              </div>

              <div className="ras-drawer-online">
                {u.isOnline ? (
                  <div className="ras-online-row">
                    <span className="ras-online-dot" />
                    <span className="ras-online-label">Online agora</span>
                  </div>
                ) : (
                  <span className="ras-last-seen">{formatLastSeen(u.lastSeen, u.lastStudied)}</span>
                )}
                <span className={sc.cls}>{sc.label}</span>
              </div>

              {(u.whatsapp || u.city || u.state || u.intendedCourse || u.birthDate) && (
                <div className="ras-drawer-lead">
                  {u.whatsapp && (
                    <a
                      className="ras-lead-whatsapp"
                      href={whatsappLink(u.whatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle size={14} />
                      {u.whatsapp}
                    </a>
                  )}
                  {(u.city || u.state) && (
                    <span className="ras-lead-chip">
                      <MapPin size={12} />
                      {[u.city, u.state].filter(Boolean).join(' - ')}
                    </span>
                  )}
                  {u.intendedCourse && (
                    <span className="ras-lead-chip">
                      <GraduationCap size={12} />
                      {u.intendedCourse}
                    </span>
                  )}
                  {u.birthDate && (
                    <span className="ras-lead-chip">
                      <Cake size={12} />
                      {new Date(u.birthDate).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
              )}

              <div className="ras-drawer-stats">
                <div className="ras-ds-item">
                  <span className="ras-ds-value">{u.cardsStudiedTotal.toLocaleString('pt-BR')}</span>
                  <span className="ras-ds-label">Cards estudados</span>
                </div>
                <div className="ras-ds-item">
                  <span className="ras-ds-value">{u.cardsStudiedThisWeek}</span>
                  <span className="ras-ds-label">Esta semana</span>
                </div>
                <div className="ras-ds-item">
                  <span className="ras-ds-value">{u.apostilasLidas}</span>
                  <span className="ras-ds-label">Apostilas lidas</span>
                </div>
                <div className="ras-ds-item">
                  <span className="ras-ds-value">{u.grants.spaces + u.grants.tabs + u.grants.subdecks}</span>
                  <span className="ras-ds-label">
                    {u.grants.tabs + u.grants.subdecks > 0 ? 'Itens comprados' : 'Campos'}
                  </span>
                </div>
              </div>

              <div className="ras-drawer-meta">
                <div className="ras-drawer-meta-row">
                  <span className="ras-drawer-meta-label">Último estudo</span>
                  <span className="ras-drawer-meta-value">
                    {u.lastStudied ? formatLastSeen(u.lastStudied, null) : 'Nunca'}
                  </span>
                </div>
                <div className="ras-drawer-meta-row">
                  <span className="ras-drawer-meta-label">Último acesso</span>
                  <span className="ras-drawer-meta-value">
                    {u.lastSeen ? new Date(u.lastSeen).toLocaleString('pt-BR') : '—'}
                  </span>
                </div>
              </div>

              <div className="ras-drawer-actions">
                {!resetConfirm && !deleteConfirm && (
                  <div className="ras-action-row">
                    <button
                      className="ras-btn-reset"
                      onClick={() => { setResetConfirm(true); setDeleteConfirm(false); }}
                    >
                      <RotateCcw size={13} /> Resetar estatísticas
                    </button>
                    <button
                      className="ras-btn-delete"
                      onClick={() => { setDeleteConfirm(true); setResetConfirm(false); setDeleteWarning(null); }}
                    >
                      <Trash2 size={13} /> Excluir usuário
                    </button>
                  </div>
                )}

                {resetConfirm && (
                  <div className="ras-reset-confirm">
                    <span className="ras-reset-confirm-text">
                      Zerar cards, semana e apostilas de {u.username || u.email}? O acesso não será afetado.
                    </span>
                    <div className="ras-delete-confirm-btns">
                      <button
                        className="ras-confirm-reset-yes"
                        onClick={() => handleResetStats(u._id)}
                        disabled={resetingId === u._id}
                      >
                        {resetingId === u._id ? 'Resetando…' : 'Confirmar'}
                      </button>
                      <button
                        className="ras-confirm-no"
                        onClick={() => setResetConfirm(false)}
                        disabled={resetingId === u._id}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {deleteConfirm && (
                  <div className="ras-delete-confirm">
                    <span className="ras-delete-confirm-text">
                      {deleteWarning ? (
                        <>
                          ⚠️ {deleteWarning.salesCount > 0 && (
                            <>Esta conta tem {deleteWarning.salesCount} venda{deleteWarning.salesCount !== 1 ? 's' : ''} confirmada{deleteWarning.salesCount !== 1 ? 's' : ''} totalizando {formatPrice(deleteWarning.totalRevenueCents)}. </>
                          )}
                          {deleteWarning.hasActiveGrants && 'Ela também tem acesso pago ativo (grants). '}
                          Excluir apaga esse histórico e o acesso em cascata, sem volta. Confirmar mesmo assim?
                        </>
                      ) : (
                        <>Excluir {u.username || u.email} permanentemente? Remove acesso Ryan e todos os dados de estudo.</>
                      )}
                    </span>
                    <div className="ras-delete-confirm-btns">
                      <button
                        className="ras-confirm-yes"
                        onClick={() => handleDelete(u._id, !!deleteWarning)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? 'Excluindo…' : deleteWarning ? 'Excluir mesmo assim' : 'Sim, excluir'}
                      </button>
                      <button
                        className="ras-confirm-no"
                        onClick={() => { setDeleteConfirm(false); setDeleteWarning(null); }}
                        disabled={isDeleting}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {toastMsg && (
                  <div className="ras-toast">{toastMsg}</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RyanAdminStats;
